import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { KuboRecommendation, KuboService, moodForRecommendation } from '../../services/kubo.service';
import { Novelty } from '../../services/novelty.service';
import { KuboAvatarComponent, KuboMood } from './kubo-avatar.component';

/** Capas que, abiertas, ponen a Kubo en "modo modal" (a la izquierda y pequeño). */
const MODALS = '[aria-modal="true"], .modal-overlay, .dialog-overlay';
/** Pies de botones de los modales: si llegan a la esquina de Kubo, sube. */
const MODAL_FOOTERS = '.modal-actions, .modal-footer, .dialog-actions, .form-actions, footer';
/** Barras fijas que Kubo no debe tapar. */
const AVOID = '[data-kubo-avoid]';

/**
 * Kubo en la esquina del panel: siempre visible (mismo enfoque que Lumi).
 * - Globo con lo que tiene que decir: recomendaciones de los módulos
 *   (onboarding, estadísticas...), novedades nuevas, avisos importantes y
 *   reacciones ("¡Listo!", "Revisa este detalle", "Un momento...").
 * - Tocarlo abre su panel: lo que toca hacer ahora, el progreso de
 *   configuración, avisos importantes y las novedades sin leer.
 * - Con un modal abierto se pasa a la izquierda, se achica, sube si el pie
 *   de botones del modal llega a su esquina y solo dice frases cortas (los
 *   mensajes largos esperan a que se cierre, para no tapar el formulario).
 *
 * `mode="local"`: versión mínima para pantallas sin el panel (p. ej.
 * restablecer contraseña): solo muestra la recomendación activa.
 */
@Component({
  selector: 'app-kubo-dock',
  standalone: true,
  imports: [CommonModule, KuboAvatarComponent],
  template: `
    @if (visible()) {
      <div class="kd" [class.compact]="compact()" [class.in-modal]="inModal()" [class.panel-open]="panelOpen()" [style.--kd-lift.px]="lift()">
        @if (panelOpen()) {
          <section class="kd-panel" role="dialog" aria-label="Kubo: avisos, novedades y progreso" (click)="$event.stopPropagation()">
            <header class="kd-head">
              <app-kubo-avatar class="kd-head-avatar" [mood]="mood()"></app-kubo-avatar>
              <div>
                <span class="kd-who">Kubo · Tu asistente</span>
                <h2>{{ kubo.firstName() ? 'Hola, ' + kubo.firstName() : '¡Hola!' }}</h2>
                <p>{{ headline() }}</p>
              </div>
              <button type="button" class="kd-x" (click)="kubo.togglePanel(false)" aria-label="Cerrar panel de Kubo"><i class="bi bi-x-lg"></i></button>
            </header>

            @if (active(); as rec) {
              <article class="kd-now" [attr.data-mood]="recMood(rec)">
                <span class="kd-label">Ahora</span>
                <strong>{{ rec.title }}</strong>
                <p>{{ rec.message }}</p>
                @if (rec.actionLabel || rec.secondaryActionLabel) {
                  <div class="kubo-card-actions">
                    @if (rec.actionLabel) { <button type="button" class="kubo-card-action" (click)="runAction(rec, $event)">{{ rec.actionLabel }} <i class="bi bi-arrow-right"></i></button> }
                    @if (rec.secondaryActionLabel) { <button type="button" class="kubo-card-action-secondary" (click)="runSecondary(rec, $event)">{{ rec.secondaryActionLabel }}</button> }
                  </div>
                }
              </article>
            }

            @if (kubo.progress(); as progress) {
              @if (pendingSteps() > 0) {
                <section class="kd-block">
                  <div class="kd-progress-row"><span>{{ progress.title }}</span><b>{{ progressPercent() }}%</b></div>
                  <div class="kd-bar"><i [style.width.%]="progressPercent()"></i></div>
                  <ol class="kd-steps">
                    @for (step of progress.steps; track step.key) {
                      <li [class.done]="step.done">
                        <span class="kd-node"><i class="bi" [class.bi-check-lg]="step.done" [class.bi-circle]="!step.done"></i></span>
                        <div><strong>{{ step.title }}</strong>@if (!step.done) { <small>{{ step.description }}</small> }</div>
                        @if (!step.done && step.go) { <button type="button" class="kd-go" (click)="goStep(step.go)" [attr.aria-label]="'Ir a ' + step.title">Ir</button> }
                      </li>
                    }
                  </ol>
                </section>
              }
            }

            @if (kubo.alerts().length) {
              <section class="kd-block">
                <h3>Importante</h3>
                @for (alert of kubo.alerts(); track alert.key) {
                  <div class="kd-alert" [attr.data-tone]="alert.tone">
                    <span class="kd-alert-icon"><i class="bi" [ngClass]="alert.icon"></i></span>
                    <div class="kd-alert-body">
                      <strong>{{ alert.title }}</strong>
                      <small>{{ alert.message }}</small>
                      <div class="kd-alert-actions">
                        @if (alert.module && alert.actionLabel) { <button type="button" class="kd-go" (click)="kubo.goModule(alert.module, alert.target)">{{ alert.actionLabel }} <i class="bi bi-arrow-right"></i></button> }
                        <button type="button" class="kd-link" (click)="kubo.dismissAlert(alert.key)">Descartar</button>
                      </div>
                    </div>
                  </div>
                }
              </section>
            }

            <section class="kd-block">
              <div class="kd-block-head">
                <h3>Novedades @if (kubo.unread()) { <span class="kd-count">{{ kubo.unread() }}</span> }</h3>
                <button type="button" class="kd-link" (click)="kubo.openNovelties()">Ver todas</button>
              </div>
              @for (item of kubo.novelties(); track item.id) {
                <div class="kd-novelty">
                  <span class="kd-dot" [attr.data-priority]="item.priority"></span>
                  <div>
                    <strong>{{ item.title }}</strong>
                    @if (item.summary) { <small>{{ item.summary }}</small> }
                    <span class="kd-meta">{{ timeAgo(item.created_at) }} · <button type="button" class="kd-link" (click)="markRead(item)">Marcar como leída</button></span>
                  </div>
                </div>
              } @empty {
                <div class="kd-empty"><app-kubo-avatar mood="exito"></app-kubo-avatar><span>Estás al día: no tienes novedades sin leer.</span></div>
              }
            </section>

            <footer class="kd-foot">
              @if (canTour()) { <button type="button" class="kd-link" (click)="kubo.startTutorial()"><i class="bi bi-mortarboard"></i> Recorrido del panel</button> }
              <button type="button" class="kd-link" (click)="kubo.toggleCompact()"><i class="bi" [class.bi-arrows-angle-expand]="compact()" [class.bi-arrows-angle-contract]="!compact()"></i> {{ compact() ? 'Kubo más grande' : 'Kubo más pequeño' }}</button>
            </footer>
          </section>
        } @else if (cardOpen() && !inModal() && active(); as rec) {
          <section class="kubo-card" role="dialog" aria-label="Recomendación de Kubo" [attr.data-mood]="recMood(rec)" (click)="$event.stopPropagation()">
            <header>
              <span class="kubo-card-eyebrow">Kubo</span>
              <button type="button" class="kubo-card-close" aria-label="Cerrar" (click)="closeCard()"><i class="bi bi-x-lg"></i></button>
            </header>
            <div class="kubo-card-body" aria-live="polite">
              <strong>{{ rec.title }}</strong>
              <p>{{ rec.message }}</p>
              @if (rec.loading) { <span class="kubo-card-loading">Un momento...</span> }
            </div>
            @if (rec.actionLabel || rec.secondaryActionLabel) {
              <div class="kubo-card-actions">
                @if (rec.actionLabel) { <button type="button" class="kubo-card-action" (click)="runAction(rec, $event)">{{ rec.actionLabel }} <i class="bi bi-arrow-right"></i></button> }
                @if (rec.secondaryActionLabel) { <button type="button" class="kubo-card-action-secondary" (click)="runSecondary(rec, $event)">{{ rec.secondaryActionLabel }}</button> }
              </div>
            }
          </section>
        } @else if (!inModal() && message(); as msg) {
          <section class="kubo-card kd-msg" role="status" aria-live="polite" [attr.data-tone]="msg.tone" (click)="$event.stopPropagation()">
            <header>
              <span class="kubo-card-eyebrow">Kubo</span>
              <button type="button" class="kubo-card-close" aria-label="Cerrar mensaje" (click)="kubo.dismissMessage()"><i class="bi bi-x-lg"></i></button>
            </header>
            <div class="kubo-card-body">
              <strong>{{ msg.title }}</strong>
              <p>{{ msg.text }}</p>
            </div>
            @if (msg.action; as action) {
              <div class="kubo-card-actions">
                <button type="button" class="kubo-card-action" (click)="runMessage(action.run)">{{ action.label }} <i class="bi bi-arrow-right"></i></button>
              </div>
            }
          </section>
        } @else if (bubble()) {
          <button type="button" class="kd-bubble" (click)="onAvatar($event)">{{ bubble() }}</button>
        }

        <button type="button" class="kd-avatar" [class.kd-wiggle]="!!bubble()" (click)="onAvatar($event)" [attr.aria-expanded]="panelOpen() || cardOpen()" [attr.aria-label]="avatarLabel()">
          <app-kubo-avatar [mood]="mood()" [animated]="true" [shadow]="true"></app-kubo-avatar>
          @if (badge()) { <span class="kd-badge">{{ badge() > 99 ? '99+' : badge() }}</span> }
        </button>
      </div>
    }
  `,
  styleUrl: './kubo-dock.component.css',
})
export class KuboDockComponent implements OnInit, OnDestroy {
  /** 'global': Kubo del panel (uno solo, en app.html). 'local': pantallas sin panel. */
  @Input() mode: 'global' | 'local' = 'global';

  readonly kubo = inject(KuboService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  readonly inModal = signal(false);
  readonly lift = signal(0);
  readonly cardOpen = signal(false);
  private readonly cyclePhrase = signal<string | null>(null);

  readonly active = computed(() => this.kubo.active());
  readonly isGlobal = computed(() => this.mode === 'global');
  readonly visible = computed(() => this.isGlobal()
    ? !this.kubo.suppressed() && !!this.auth.currentUser() && !this.auth.trialExpired() && !this.auth.subscriptionRestricted()
    : !this.kubo.dockMounted() && !!this.active());
  readonly panelOpen = computed(() => this.isGlobal() && this.kubo.panelOpen() && !this.inModal());
  readonly compact = computed(() => this.kubo.compact() || this.inModal());
  readonly message = computed(() => (this.isGlobal() ? this.kubo.message() : null));
  readonly bubble = computed(() => (this.isGlobal() ? this.kubo.phrase() : null) ?? this.cyclePhrase());
  readonly badge = computed(() => (this.isGlobal() ? this.kubo.badge() : 0));
  readonly pendingSteps = computed(() => this.kubo.pendingSteps());
  readonly mood = computed<KuboMood>(() => {
    if (this.isGlobal()) return this.kubo.mood();
    return moodForRecommendation(this.active());
  });
  readonly progressPercent = computed(() => {
    const steps = this.kubo.progress()?.steps ?? [];
    return steps.length ? Math.round((steps.filter(s => s.done).length / steps.length) * 100) : 100;
  });
  readonly headline = computed(() => {
    const parts: string[] = [];
    const unread = this.kubo.unread();
    if (this.pendingSteps()) parts.push(this.pendingSteps() === 1 ? 'te falta 1 paso de configuración' : `te faltan ${this.pendingSteps()} pasos de configuración`);
    if (this.kubo.alerts().length) parts.push(this.kubo.alerts().length === 1 ? 'hay 1 aviso importante' : `hay ${this.kubo.alerts().length} avisos importantes`);
    if (unread) parts.push(unread === 1 ? 'tienes 1 novedad sin leer' : `tienes ${unread} novedades sin leer`);
    if (!parts.length) return 'Todo está al día. Aquí estaré si me necesitas.';
    const text = parts.join(', ');
    return text.charAt(0).toUpperCase() + text.slice(1) + '.';
  });

  private lastActiveId: string | null = null;
  private bubbleInterval?: ReturnType<typeof setInterval>;
  private bubbleTimeout?: ReturnType<typeof setTimeout>;
  private observer: MutationObserver | null = null;
  private frame = 0;

  constructor() {
    // Recomendación NUEVA (id distinto): se abre una vez para explicarla
    // (menos las de prioridad baja). Si trae frases, sigue recordando con el
    // globo pequeño mientras la tarjeta esté cerrada.
    effect(() => {
      const rec = this.active();
      const id = rec?.id ?? null;
      if (id === this.lastActiveId) return;
      this.lastActiveId = id;
      this.stopBubbles();
      if (!rec) { this.cardOpen.set(false); return; }
      if (rec.bubbles?.length) this.startBubbles(rec.bubbles);
      if ((rec.priority ?? 'medium') !== 'low') {
        this.cardOpen.set(true);
        if (this.isGlobal()) this.kubo.togglePanel(false);
      }
    });
  }

  ngOnInit(): void {
    if (!this.isGlobal()) return;
    this.kubo.dockMounted.set(true);
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef), filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
      this.kubo.togglePanel(false);
      this.kubo.routeChanged();
    });
    this.kubo.routeChanged();
    // Modales y barras fijas: se revisa por cuadro, fuera de Angular, y solo
    // se vuelve a la zona si algo cambió.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-modal', 'hidden'] });
      window.addEventListener('resize', this.schedule, { passive: true });
    });
    this.schedule();
  }

  ngOnDestroy(): void {
    if (this.isGlobal()) this.kubo.dockMounted.set(false);
    this.stopBubbles();
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.schedule);
  }

  // ------------------------------------------------------------ acciones
  onAvatar(event: Event): void {
    event.stopPropagation();
    if (!this.isGlobal()) { this.cardOpen.update(v => !v); this.cyclePhrase.set(null); return; }
    if (this.cardOpen()) { this.closeCard(); return; }
    this.kubo.dismissMessage();
    this.cyclePhrase.set(null);
    this.kubo.togglePanel();
  }

  closeCard(): void { this.cardOpen.set(false); }

  runAction(rec: KuboRecommendation, event: Event): void {
    event.stopPropagation();
    this.cardOpen.set(false);
    this.kubo.togglePanel(false);
    rec.onAction?.();
  }

  runSecondary(rec: KuboRecommendation, event: Event): void {
    event.stopPropagation();
    this.cardOpen.set(false);
    this.kubo.togglePanel(false);
    rec.onSecondaryAction?.();
  }

  runMessage(run: () => void): void {
    this.kubo.dismissMessage();
    run();
  }

  goStep(go: () => void): void {
    this.kubo.togglePanel(false);
    go();
  }

  markRead(item: Novelty): void { this.kubo.markNoveltyRead(item); }

  recMood(rec: KuboRecommendation): KuboMood { return moodForRecommendation(rec); }

  canTour(): boolean { const role = this.kubo.role(); return role === 'owner' || role === 'super_admin'; }

  avatarLabel(): string {
    if (!this.isGlobal()) return this.cardOpen() ? 'Cerrar mensaje de Kubo' : (this.active()?.title || 'Kubo');
    const badge = this.badge();
    return this.panelOpen() ? 'Cerrar panel de Kubo' : badge ? `Kubo, tu asistente: ${badge} pendiente(s). Toca para ver avisos y novedades` : 'Kubo, tu asistente: toca para ver avisos y novedades';
  }

  timeAgo(iso: string): string {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const min = Math.round(diff / 60000);
    if (min < 1) return 'Ahora';
    if (min < 60) return `Hace ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `Hace ${h} h`;
    const d = Math.round(h / 24);
    return d === 1 ? 'Ayer' : `Hace ${d} días`;
  }

  // ------------------------------------------------------------ frases de recordatorio
  private startBubbles(phrases: string[]): void {
    let index = 0;
    const show = () => {
      if (this.cardOpen() || this.panelOpen() || this.message()) return;
      this.cyclePhrase.set(phrases[index++ % phrases.length]);
      this.bubbleTimeout = setTimeout(() => this.cyclePhrase.set(null), 4500);
    };
    this.bubbleTimeout = setTimeout(show, 2200);
    this.bubbleInterval = setInterval(show, 19000);
  }

  private stopBubbles(): void {
    if (this.bubbleInterval) clearInterval(this.bubbleInterval);
    if (this.bubbleTimeout) clearTimeout(this.bubbleTimeout);
    this.bubbleInterval = undefined;
    this.bubbleTimeout = undefined;
    this.cyclePhrase.set(null);
  }

  // ------------------------------------------------------------ modales y barras fijas
  private schedule = (): void => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.measure(); });
  };

  private measure(): void {
    const own = this.host.nativeElement as HTMLElement;
    const h = window.innerHeight;
    const modals = Array.from(document.querySelectorAll<HTMLElement>(MODALS))
      .filter(m => !own.contains(m) && !m.closest('app-owner-onboarding') && m.getClientRects().length > 0 && getComputedStyle(m).visibility !== 'hidden');
    const top = modals.at(-1) ?? null;
    let lift = 0;
    if (top) {
      const corner = 16 + (window.innerWidth <= 640 ? 52 : 64);
      for (const footer of Array.from(top.querySelectorAll<HTMLElement>(MODAL_FOOTERS))) {
        const r = footer.getBoundingClientRect();
        if (r.height && r.left < corner && r.bottom > h - 150) lift = Math.max(lift, Math.round(h - r.top + 6));
      }
    } else {
      for (const el of Array.from(document.querySelectorAll<HTMLElement>(AVOID))) {
        const r = el.getBoundingClientRect();
        if (r.height && r.top < h && r.bottom > h - 150) lift = Math.max(lift, Math.round(h - r.top + 8));
      }
    }
    const inModal = !!top;
    if (inModal === this.inModal() && lift === this.lift()) return;
    this.zone.run(() => {
      this.inModal.set(inModal);
      this.lift.set(lift);
    });
  }

  // ------------------------------------------------------------ teclado y clic fuera
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.panelOpen()) this.kubo.togglePanel(false);
    else if (this.cardOpen()) this.closeCard();
  }

  @HostListener('document:click', ['$event'])
  onOutside(event: MouseEvent): void {
    if (!this.panelOpen() && !this.cardOpen()) return;
    if (this.host.nativeElement.contains(event.target as Node)) return;
    this.kubo.togglePanel(false);
    this.closeCard();
  }

  @HostListener('document:visibilitychange')
  onVisibility(): void {
    if (this.isGlobal() && !document.hidden) this.kubo.refresh();
  }
}
